import { ImageOff, Trash2, Upload } from "lucide-react";
import { useRef } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { assetContentUrl, useBranding, useDeleteAsset, useUploadAsset } from "@/hooks/use-branding";
import { ASSET_SLUGS, LOGO_CONTENT_TYPES, LOGO_MAX_BYTES } from "@/schemas/branding";

const ACCEPT = LOGO_CONTENT_TYPES.join(",");
const MAX_MB = Math.round(LOGO_MAX_BYTES / 1_000_000);

export function BrandingPage() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { data, isLoading, isError, refetch } = useBranding();
  const uploadMutation = useUploadAsset();
  const deleteMutation = useDeleteAsset();

  function onPick(file: File | undefined) {
    if (!file) return;
    uploadMutation.mutate({ slug: ASSET_SLUGS.homepageLogo, file });
    // Reset so re-selecting the same file still fires a change event.
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  if (isLoading) {
    return (
      <div>
        <h1 className="font-display text-3xl font-bold">Branding</h1>
        <div className="mt-4 space-y-3">
          <div className="h-40 animate-pulse rounded bg-muted" />
          <div className="h-9 w-40 animate-pulse rounded bg-muted" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <h1 className="font-display text-3xl font-bold">Branding</h1>
        <ErrorState message="Couldn't load branding settings." onRetry={() => refetch()} />
      </div>
    );
  }

  const logo = data?.logo ?? null;
  // Cache-bust the preview by version so a fresh upload renders immediately.
  const previewUrl = logo
    ? `${assetContentUrl(ASSET_SLUGS.homepageLogo)}${assetContentUrl(ASSET_SLUGS.homepageLogo).includes("?") ? "&" : "?"}v=${encodeURIComponent(logo.updatedOn)}`
    : null;

  return (
    <div>
      <div className="rounded-md border bg-card p-4">
        <h1 className="font-display text-3xl font-bold">Branding</h1>
        <p className="mt-1 text-muted-foreground">
          Upload a logo to display on the home page. It replaces the default MEMIC text. Supported
          formats: PNG, JPEG, SVG, WebP (max {MAX_MB} MB).
        </p>
      </div>

      <Card className="mt-6">
        <CardHeader />
        <CardContent>
          <div className="grid gap-4">
            <span className="font-medium text-sm">Home page logo</span>

            {/* Current state preview */}
            <div className="flex min-h-32 items-center justify-center rounded-md border border-dashed bg-muted/30 p-6">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt={logo?.fileName ?? "Current logo"}
                  className="max-h-24 max-w-full object-contain"
                />
              ) : (
                <div className="flex flex-col items-center gap-2 text-center text-muted-foreground">
                  <ImageOff className="size-8" aria-hidden="true" />
                  <span className="text-sm">
                    No logo uploaded — the home page shows a placeholder.
                  </span>
                </div>
              )}
            </div>

            {logo && (
              <p className="text-muted-foreground text-xs">
                Current file: <span className="font-medium">{logo.fileName}</span>
              </p>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                className="hidden"
                onChange={(e) => onPick(e.target.files?.[0])}
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadMutation.isPending}
              >
                <Upload className="size-4" />
                {uploadMutation.isPending ? "Uploading..." : logo ? "Replace logo" : "Upload logo"}
              </Button>
              {logo && (
                <Button
                  variant="outline"
                  onClick={() => deleteMutation.mutate({ slug: ASSET_SLUGS.homepageLogo })}
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 className="size-4" />
                  {deleteMutation.isPending ? "Removing..." : "Remove logo"}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
